import React, { useState } from 'react';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';

interface PublicSession {
  sessionId: string;
  tenantName?: string;
  amountUsdCents: number;
  accepts: string;
  chosenRail?: string;
  whopCheckoutUrl?: string;
  status: string;
}

interface WhopRailPanelProps {
  session: PublicSession;
  onSelectOther?: () => void;
}

export const WhopRailPanel: React.FC<WhopRailPanelProps> = ({ session, onSelectOther }) => {
  const [loading, setLoading] = useState(false);
  const usdStr = (session.amountUsdCents / 100).toFixed(2);

  const handleWhopCheckout = async () => {
    setLoading(true);
    try {
      let url = session.whopCheckoutUrl;
      if (!url) {
        const res = await api.post(`/api/v1/whop-checkout/${session.sessionId}`);
        url = res.whopCheckoutUrl ?? res.url;
      }
      if (url) window.location.href = url;
    } catch (e) {
      toast.error('Failed to initiate Whop checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='flex flex-col gap-5'>
      {/* Whop branding */}
      <div className='flex flex-col items-center gap-2 py-2'>
        <div
          className='w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold text-white'
          style={{ background: 'var(--pp-whop)' }}
        >
          W
        </div>
        <div className='text-center'>
          <div className='font-semibold text-base' style={{ color: 'var(--pp-text)' }}>
            Continue with Whop
          </div>
          <div className='text-sm mt-0.5' style={{ color: 'var(--pp-text-muted)' }}>
            Complete your payment securely through Whop
          </div>
        </div>
      </div>

      {/* Details table */}
      <div
        className='rounded-lg border divide-y text-sm'
        style={{ borderColor: 'var(--pp-border)', background: 'var(--pp-surface)' }}
      >
        <div className='flex justify-between px-4 py-2.5'>
          <span style={{ color: 'var(--pp-text-muted)' }}>Amount</span>
          <span className='font-semibold' style={{ fontFamily: 'var(--pp-mono)', color: 'var(--pp-text)' }}>
            ${usdStr}
          </span>
        </div>
        <div className='flex justify-between px-4 py-2.5'>
          <span style={{ color: 'var(--pp-text-muted)' }}>Merchant</span>
          <span style={{ color: 'var(--pp-text)' }}>{session.tenantName ?? '—'}</span>
        </div>
        <div className='flex justify-between px-4 py-2.5'>
          <span style={{ color: 'var(--pp-text-muted)' }}>Session</span>
          <span style={{ fontFamily: 'var(--pp-mono)', color: 'var(--pp-text)', fontSize: '0.75rem' }}>
            {session.sessionId}
          </span>
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={handleWhopCheckout}
        disabled={loading}
        className='w-full py-2.5 px-4 rounded text-sm font-semibold text-center text-white transition-opacity hover:opacity-90'
        style={{ background: 'var(--pp-action)', opacity: loading ? 0.7 : 1 }}
      >
        {loading ? 'Opening Whop…' : 'Pay with card on Whop'}
      </button>

      {onSelectOther && session.accepts.split(',').filter((r) => r !== 'whop').length > 0 && (
        <button
          onClick={onSelectOther}
          className='w-full py-2 text-sm text-center transition-colors hover:underline'
          style={{ color: 'var(--pp-text-muted)' }}
        >
          Choose another payment method
        </button>
      )}
    </div>
  );
};

export default WhopRailPanel;
